import type { SpaceConfig } from "@cloudnest/stone-flicking-core-engine/types";

export interface CircularCanvasLayout {
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly centerPixelX: number;
  readonly centerPixelY: number;
  readonly pixelRadius: number;
  readonly scale: number;
  readonly boardCenterX: number;
  readonly boardCenterY: number;
  readonly boardRadius: number;
  readonly rotationAngle: number;
}

export function computeCircularLayout(
  canvasW: number,
  canvasH: number,
  padding: number,
  spaceConfig: SpaceConfig,
  playerIndex: number,
  playerCount: number,
): CircularCanvasLayout {
  const size = Math.min(canvasW, canvasH);
  const pixelRadius = (size - padding * 2) / 2;
  const scale = pixelRadius / spaceConfig.radius;

  const sectorAngle = (2 * Math.PI) / playerCount;
  const playerCenterAngle = playerIndex * sectorAngle + sectorAngle / 2;
  const rotationAngle = (3 * Math.PI) / 2 - playerCenterAngle;

  return {
    canvasWidth: canvasW,
    canvasHeight: canvasH,
    centerPixelX: canvasW / 2,
    centerPixelY: canvasH / 2,
    pixelRadius,
    scale,
    boardCenterX: spaceConfig.centerX,
    boardCenterY: spaceConfig.centerY,
    boardRadius: spaceConfig.radius,
    rotationAngle,
  };
}

export function boardToCanvas(
  boardX: number,
  boardY: number,
  layout: CircularCanvasLayout,
): { x: number; y: number } {
  const dx = boardX - layout.boardCenterX;
  const dy = boardY - layout.boardCenterY;

  const cos = Math.cos(layout.rotationAngle);
  const sin = Math.sin(layout.rotationAngle);
  const rotatedX = dx * cos - dy * sin;
  const rotatedY = dx * sin + dy * cos;

  return {
    x: layout.centerPixelX + rotatedX * layout.scale,
    y: layout.centerPixelY - rotatedY * layout.scale,
  };
}

export function canvasToBoard(
  canvasX: number,
  canvasY: number,
  layout: CircularCanvasLayout,
): { x: number; y: number } {
  const px = (canvasX - layout.centerPixelX) / layout.scale;
  const py = -(canvasY - layout.centerPixelY) / layout.scale;

  const cos = Math.cos(-layout.rotationAngle);
  const sin = Math.sin(-layout.rotationAngle);
  const boardDx = px * cos - py * sin;
  const boardDy = px * sin + py * cos;

  return {
    x: layout.boardCenterX + boardDx,
    y: layout.boardCenterY + boardDy,
  };
}
